import type { SubmitFeedbackInput } from "../types";

/**
 * Satu-satunya tempat widget bicara dengan server.
 *
 * Bentuk aslinya (brightflow) memakai presigned URL S3: minta URL, PUT ke storage,
 * kirim kuncinya. Di sini unggahannya lewat BE kami sendiri — storage Maua memang
 * dialirkan dari sana — jadi `requestUploadUrl` + `uploadBlob` diringkas jadi satu
 * `uploadMedia`, dan yang dipegang widget adalah id baris media, bukan kunci objek.
 *
 * Kunci proyek dikirim lewat header `X-Feedback-Key` pada semua panggilan `fetch`;
 * ia baru turun ke query pada URL yang dipasang di `<img>`/`<video>`, yang memang
 * tidak bisa membawa header.
 */

export type WidgetApiConfig = {
  apiBase: string;
  projectKey: string;
  /**
   * Proyek ini membuka daftar penuhnya tanpa kode penguji (server: `public_list`).
   * Diisi dari `/feedback/config` saat boot. Nilainya hanya menentukan apakah
   * widget REPOT bertanya — yang memutuskan tetap server.
   */
  publicList?: boolean;
};

function headers(cfg: WidgetApiConfig, json = false): Record<string, string> {
  const h: Record<string, string> = { "X-Feedback-Key": cfg.projectKey };
  if (json) h["Content-Type"] = "application/json";
  return h;
}

async function fail(res: Response, what: string): Promise<never> {
  const body = (await res.json().catch(() => null)) as { error?: string } | null;
  throw new Error(body?.error ?? `${what} failed (${res.status})`);
}

export type WidgetConfig = {
  position: string;
  primaryColor: string | null;
  buttonLabel: string | null;
  appTitle: string | null;
  /** Proyek ini punya kode penguji internal; tanpa ini panel kode disembunyikan. */
  internalMode: boolean;
  /** Daftar penuh terbuka tanpa kode sama sekali. Lihat `public_list` di BE. */
  publicList?: boolean;
};

export async function fetchWidgetConfig(cfg: WidgetApiConfig): Promise<WidgetConfig | null> {
  const res = await fetch(`${cfg.apiBase}/feedback/config?key=${encodeURIComponent(cfg.projectKey)}`, {
    headers: headers(cfg),
  }).catch(() => null);
  if (!res || !res.ok) return null; // pintunya tertutup/kunci asing → widget diam saja
  return (await res.json()) as WidgetConfig;
}

/**
 * Unggah tangkapan layar atau rekaman. Memulangkan id media untuk dititipkan ke
 * kiriman berikutnya; media yang kirimannya tidak pernah datang akan disapu server.
 */
export async function uploadMedia(cfg: WidgetApiConfig, blob: Blob, filename: string): Promise<string> {
  const form = new FormData();
  form.set("file", new File([blob], filename, { type: blob.type || "application/octet-stream" }));
  const res = await fetch(`${cfg.apiBase}/feedback/upload`, { method: "POST", headers: headers(cfg), body: form });
  if (!res.ok) await fail(res, "upload");
  return ((await res.json()) as { id: string }).id;
}

export async function verifyTeamCode(cfg: WidgetApiConfig, code: string): Promise<boolean> {
  try {
    const res = await fetch(`${cfg.apiBase}/feedback/verify-code`, {
      method: "POST",
      headers: headers(cfg, true),
      body: JSON.stringify({ key: cfg.projectKey, code }),
    });
    if (!res.ok) return false;
    return Boolean(((await res.json()) as { valid?: boolean }).valid);
  } catch {
    // Jaringan mati bukan alasan mempromosikan orang jadi penguji internal —
    // lagi pula server memeriksa ulang kodenya saat kiriman masuk.
    return false;
  }
}

export async function submitFeedback(
  cfg: WidgetApiConfig,
  payload: Omit<SubmitFeedbackInput, "key">,
): Promise<{ id: string; cardId: string | null }> {
  const res = await fetch(`${cfg.apiBase}/feedback`, {
    method: "POST",
    headers: headers(cfg, true),
    body: JSON.stringify({ ...payload, key: cfg.projectKey }),
  });
  if (!res.ok) await fail(res, "submit");
  return (await res.json()) as { id: string; cardId: string | null };
}

export type FeedbackPinSummary = {
  xPct: number;
  yPct: number;
  note?: string;
  pageX?: number;
  pageY?: number;
  pageWidth?: number;
};

export type FeedbackMedia = { id: string; kind: "image" | "video" | string; mime: string };

export type FeedbackListItem = {
  id: string;
  type: string;
  severity: string;
  status: string;
  source?: string;
  title: string;
  description: string | null;
  createdAt: number;
  url: string | null;
  reporter: { name: string | null; email: string | null } | null;
  media: FeedbackMedia[];
  /** Turunan dari `media` — dipakai daftar untuk menampilkan ikon lampiran. */
  hasScreenshot: boolean;
  hasVideo: boolean;
  pins: FeedbackPinSummary[];
};

export type AllFeedbackItem = FeedbackListItem;

export type AllFeedbackPage = {
  unresolvedCount: number;
  totalCount: number;
  hasMore: boolean;
  nextOffset: number | null;
  feedback: AllFeedbackItem[];
};

type RawItem = Omit<FeedbackListItem, "hasScreenshot" | "hasVideo">;

/** Server mengirim daftar media; ikon di daftar cuma perlu tahu ADA atau tidak. */
function decorate(raw: RawItem): FeedbackListItem {
  const media = raw.media ?? [];
  return {
    ...raw,
    media,
    hasScreenshot: media.some((m) => m.kind === "image"),
    hasVideo: media.some((m) => m.kind === "video"),
  };
}

export async function listMyFeedback(
  cfg: WidgetApiConfig,
  browserId: string,
  options: { url?: string } = {},
): Promise<FeedbackListItem[]> {
  const url = new URL(`${cfg.apiBase}/feedback/mine`);
  url.searchParams.set("key", cfg.projectKey);
  url.searchParams.set("browserId", browserId);
  if (options.url) url.searchParams.set("url", options.url);
  const res = await fetch(url.toString(), { headers: headers(cfg) });
  if (!res.ok) await fail(res, "list");
  return ((await res.json()) as { feedback: RawItem[] }).feedback.map(decorate);
}

/**
 * Seluruh kotak masuk proyek — menuntut kode penguji internal.
 *
 * Tanpa kode server menjawab 403, dan itu memang perilaku yang benar: daftar ini
 * memuat laporan orang lain. Widget memperlakukan 403 sebagai "belum berhak",
 * bukan sebagai kegagalan yang perlu diteriakkan.
 */
export async function listAllFeedback(
  cfg: WidgetApiConfig,
  opts: { status?: string; offset?: number; teamCode?: string } = {},
): Promise<AllFeedbackPage> {
  const kosong: AllFeedbackPage = { unresolvedCount: 0, totalCount: 0, hasMore: false, nextOffset: null, feedback: [] };
  // Tanpa kode DAN tanpa `publicList`, server pasti menjawab 403 — jangan
  // menyusahkan jaringan untuk itu. Kalau salah satunya ada, biar server yang
  // memutuskan: widget tidak pernah jadi penjaga pintu.
  if (!opts.teamCode && !cfg.publicList) return kosong;
  const url = new URL(`${cfg.apiBase}/feedback/all`);
  url.searchParams.set("key", cfg.projectKey);
  if (opts.teamCode) url.searchParams.set("code", opts.teamCode);
  if (opts.status) url.searchParams.set("status", opts.status);
  if (opts.offset) url.searchParams.set("offset", String(opts.offset));
  const res = await fetch(url.toString(), { headers: headers(cfg) });
  if (res.status === 403) return kosong;
  if (!res.ok) await fail(res, "list-all");
  const page = (await res.json()) as Omit<AllFeedbackPage, "feedback"> & { feedback: RawItem[] };
  return { ...page, feedback: page.feedback.map(decorate) };
}

/**
 * URL media untuk dipasang langsung di `<img>`/`<video>`.
 *
 * Bukan `fetch`: gambarnya dirender peramban, dan menariknya sebagai blob dulu
 * berarti menahan rekaman 40 MB di memori halaman orang. Kunci dan bukti akses
 * ikut di query karena elemen media tidak bisa membawa header.
 */
export function mediaUrl(
  cfg: WidgetApiConfig,
  mediaId: string,
  opts: { browserId?: string; teamCode?: string } = {},
): string {
  const url = new URL(`${cfg.apiBase}/feedback/media/${encodeURIComponent(mediaId)}`);
  url.searchParams.set("key", cfg.projectKey);
  if (opts.browserId) url.searchParams.set("browserId", opts.browserId);
  if (opts.teamCode) url.searchParams.set("code", opts.teamCode);
  return url.toString();
}
